import PDFDocument from "pdfkit/js/pdfkit.standalone";
import { formatCnpjCpf, formatPrice } from "../../utils";

function textoSeguro(valor) {
  return String(valor ?? "").trim();
}

function numeroSeguro(valor) {
  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : 0;
}

function formatarData(data) {
  if (!data) return "";

  const dataFormatada = new Date(data);
  if (Number.isNaN(dataFormatada.getTime())) return "";

  return dataFormatada.toLocaleDateString("pt-BR");
}

function totalItensLista(listaProdutos) {
  if (!listaProdutos?.length) return 0;

  return listaProdutos.reduce((total, item) => {
    const subtotal =
      item?.subtotal !== undefined &&
      item?.subtotal !== null &&
      item?.subtotal !== ""
        ? numeroSeguro(item.subtotal)
        : numeroSeguro(item.quantidade) * numeroSeguro(item.valor);

    return total + subtotal;
  }, 0);
}

function totalVenda(dto) {
  const freteNota = dto?.freteCompoeNota ? numeroSeguro(dto?.frete) : 0;
  const subtotalProdutos = totalItensLista(dto?.nfSaidaItens || []);
  const subtotalDesconto = numeroSeguro(dto?.desconto);
  const subtotalAcrescimo = numeroSeguro(dto?.acrescimo);
  const subtotalDespesas = numeroSeguro(dto?.despesas);

  return {
    subTotal: subtotalProdutos,
    total:
      subtotalProdutos -
      subtotalDesconto +
      subtotalAcrescimo +
      subtotalDespesas +
      freteNota,
  };
}

function concatenarBytes(chunks) {
  const tamanhoTotal = chunks.reduce((total, chunk) => total + chunk.length, 0);
  const resultado = new Uint8Array(tamanhoTotal);
  let offset = 0;

  chunks.forEach((chunk) => {
    resultado.set(chunk, offset);
    offset += chunk.length;
  });

  return resultado;
}

function bytesParaBase64(bytes) {
  let binario = "";
  const tamanhoFatia = 0x8000;

  for (let indice = 0; indice < bytes.length; indice += tamanhoFatia) {
    const fatia = bytes.subarray(indice, indice + tamanhoFatia);
    binario += String.fromCharCode(...fatia);
  }

  return btoa(binario);
}

function mapearParaDto({ pdvEntity, dadosEmpresa }) {
  const clienteNome =
    pdvEntity?.clienteNome ||
    pdvEntity?.clienteSelecionado?.nomeRazaoSocial ||
    "";
  const clienteCnpjCpf =
    pdvEntity?.clienteCnpjCpf || pdvEntity?.clienteSelecionado?.cnpjCpf || "";

  return {
    fantasia: textoSeguro(
      dadosEmpresa?.fantasia || dadosEmpresa?.nomeRazaoSocial,
    ),
    cnpjCpf: textoSeguro(dadosEmpresa?.cnpjCpf),
    enderecoLogradouro: textoSeguro(dadosEmpresa?.enderecoLogradouro),
    enderecoLogradouroNumero: textoSeguro(
      dadosEmpresa?.enderecoLogradouroNumero,
    ),
    enderecoCidade: textoSeguro(dadosEmpresa?.enderecoCidade),
    enderecoUF: textoSeguro(dadosEmpresa?.enderecoUF || dadosEmpresa?.uf),
    dataSaida: pdvEntity?.dataSaida || pdvEntity?.dataEmissao,
    numeroDocumento: textoSeguro(pdvEntity?.numeroDocumento),
    serieDocumento: textoSeguro(pdvEntity?.serieDocumento),
    clienteNome: textoSeguro(clienteNome),
    clienteCnpjCpf: textoSeguro(clienteCnpjCpf),
    freteCompoeNota: Boolean(pdvEntity?.freteCompoeNota),
    frete: numeroSeguro(pdvEntity?.frete),
    desconto: numeroSeguro(pdvEntity?.desconto),
    acrescimo: numeroSeguro(pdvEntity?.acrescimo),
    despesas: numeroSeguro(pdvEntity?.despesas),
    nfSaidaItens: (pdvEntity?.nfSaidaItens || []).map((item) => ({
      codigo: textoSeguro(item?.codigo),
      descricao: textoSeguro(item?.descricao),
      quantidade: numeroSeguro(item?.quantidade),
      unidadeCom: textoSeguro(item?.unidadeCom || item?.unidade || "UN"),
      valor: numeroSeguro(item?.valor),
      subtotal:
        item?.subtotal !== undefined &&
        item?.subtotal !== null &&
        item?.subtotal !== ""
          ? numeroSeguro(item.subtotal)
          : numeroSeguro(item?.quantidade) * numeroSeguro(item?.valor),
    })),
  };
}

export async function generateComprovanteDebito({
  pdvEntity,
  dadosEmpresa,
  pagamentosAprazo,
}) {
  const dto = mapearParaDto({ pdvEntity, dadosEmpresa });
  const comprimentoExtra = 15 * pagamentosAprazo.length;
  const comprimento = 170 + comprimentoExtra;

  return new Promise((resolve, reject) => {
    try {
      const doc = new PDFDocument({
        size: [203.77, comprimento],
        margin: 0,
        bufferPages: true,
        autoFirstPage: true,
        info: {
          Title: `DANFE - ${dto.numeroDocumento}`,
          Author: "SIAF Evolution",
          Subject: "Documento Auxiliar da Nota Fiscal Eletrônica",
          Keywords: "danfe, nfe, documento fiscal",
          Creator: "SIAF Evolution",
          Producer: "SIAF Evolution",
        },
      });

      const chunks = [];
      doc.on("data", (chunk) => {
        chunks.push(
          chunk instanceof Uint8Array ? chunk : new Uint8Array(chunk),
        );
      });
      doc.on("error", (error) => reject(error));
      doc.on("end", () => {
        try {
          resolve(bytesParaBase64(concatenarBytes(chunks)));
        } catch (error) {
          reject(error);
        }
      });

      const margemEsquerda = 1;
      const margemTopo = 10;
      const margemDireita = doc?.page?.width - 1;
      const ajusteX = 2;
      const largura = 200;
      let inicio = 0;
      let inicioResumoTotais = 75;

      function linhaHorizontal(x1, x2, y) {
        const yPosicao = margemTopo + y;
        const xInicial = margemEsquerda + x1;
        const xFinal = margemDireita + x2;
        doc.moveTo(xInicial, yPosicao).lineTo(xFinal, yPosicao).stroke();
      }

      function campo(
        texto,
        x,
        y,
        larguraTexto = 100,
        alinhamento = "center",
        tamanho = 6,
      ) {
        doc
          .font("Helvetica-Bold")
          .fillColor("black")
          .fontSize(tamanho)
          .text(texto || "", margemEsquerda + x + ajusteX, margemTopo + y, {
            width: larguraTexto,
            align: alinhamento,
          });
      }

      function normal(
        texto,
        x,
        y,
        larguraTexto = 100,
        alinhamento = "center",
        tamanho = 6,
      ) {
        doc
          .font("Helvetica")
          .fillColor("black")
          .fontSize(tamanho)
          .text(texto || "", margemEsquerda + x + ajusteX, margemTopo + y, {
            width: larguraTexto,
            align: alinhamento,
            lineGap: -1,
          });
      }

      function desenhaCabecalho() {
        const enderecoCompleto = `${dto.enderecoLogradouro}, ${dto.enderecoLogradouroNumero}, ${dto.enderecoCidade} / ${dto.enderecoUF}`;

        normal(dto.fantasia, 1, inicio, largura, "center", 6);
        campo(formatCnpjCpf(dto.cnpjCpf), 1, inicio + 7, largura, "center", 6);
        campo(enderecoCompleto, 1, inicio + 14, largura, "center", 6);
        campo(
          `NF-SAIDA Nº${dto.numeroDocumento} SÉRIE: ${dto.serieDocumento}`,
          1,
          inicio + 21,
          largura,
          "center",
          6,
        );
        normal(
          formatarData(dto.dataSaida),
          1,
          inicio + 28,
          largura,
          "center",
          6,
        );
        campo("COMPROVANTE DE DÉBITO", 1, inicio + 37, largura, "center", 10);
        campo("DADOS DO CLIENTE", 1, inicio + 49, largura, "center", 7);
        normal(dto.clienteNome, 1, inicio + 56, largura, "center", 6);
        normal(
          formatCnpjCpf(dto.clienteCnpjCpf),
          1,
          inicio + 63,
          largura,
          "center",
          6,
        );
      }

      function desenhaResumoTotal(total, subTotal) {
        campo("VALOR A PAGAR", 2, inicioResumoTotais, largura - 105, "left", 7);
        const totalPagamento = pagamentosAprazo.reduce(
          (acumulado, pagamento) =>
            acumulado + numeroSeguro(pagamento.valorTitulo),
          0,
        );
        campo(
          formatPrice(totalPagamento, false) || "0,00",
          105,
          inicioResumoTotais,
          largura - 110,
          "right",
          7,
        );
      }

      function desenharPacelasPagamento() {
        linhaHorizontal(0, 0, inicioResumoTotais + 15);
        campo("PARCELAS", 2, inicioResumoTotais + 20, largura - 105, "left", 7);
        campo(
          "VENCIMENTO",
          77,
          inicioResumoTotais + 20,
          largura - 105,
          "left",
          7,
        );
        campo("VALOR", 170, inicioResumoTotais + 20, largura - 105, "left", 7);

        let y = inicioResumoTotais + 30;
        for (const pagamento of pagamentosAprazo) {
          campo(pagamento.numeroTitulo, 2, y, largura - 200, "left", 6);
          campo(
            formatarData(pagamento.dataVencimento),
            79,
            y,
            largura - 160,
            "center",
            6,
          );
          campo(
            formatPrice(pagamento.valorTitulo, false),
            150,
            y,
            largura - 155,
            "right",
            6,
          );
          y += 10;
        }
        return y;
      }

      desenhaCabecalho();
      const { total, subTotal } = totalVenda(dto);
      desenhaResumoTotal(total, subTotal);
      const finalPacelas = desenharPacelasPagamento();
      linhaHorizontal(0, 0, finalPacelas + 40);
      campo("ASSINATURA", 2, finalPacelas + 45, largura, "center", 7);

      doc.end();
    } catch (error) {
      reject(error);
    }
  });
}
